import { environmentInfo } from '@ms-cloudpack/environment';
import { writeJson } from '@ms-cloudpack/json-utilities';
import type { UserPreferences } from '@ms-cloudpack/common-types';
import { _getMockUserPreferencesDir, getUserPreferencesPath } from './getUserPreferencesPath.js';
import { allUserPreferences, userPreferencesSchemaUrl } from '../constants.js';
import { readUserPreferences } from './readUserPreferences.js';
import path from 'path';
import { isFolderSync } from '@ms-cloudpack/path-utilities';

/**
 * Validate and write an update to a user preference item.
 * Creates the file if it doesn't already exist.
 *
 * Throws an error if the key is not a valid user preference name.
 */
export async function updateUserPreference(params: {
  key: keyof UserPreferences;
  /** Update `key` to `newValue` */
  newValue?: string;
  /** Delete `key` */
  deleteValue?: boolean;
}): Promise<void> {
  const { key, deleteValue } = params;

  if (environmentInfo.isJest && !_getMockUserPreferencesDir()) {
    // Prevent tests from accidentally messing with real global state...
    throw new Error('Tests must call setMockUserPreferencesDir before calling updateUserPreference.');
  }

  // The key is user input which could potentially be invalid, despite the types.
  if (!allUserPreferences[key]) {
    throw new Error(`"${key}" is not a valid user preference name.`);
  }

  let value = params.newValue?.trim();

  if (!deleteValue) {
    if (value === undefined) {
      throw new Error('Must specify newValue or deleteValue (this is a bug in Cloudpack)');
    }
    value = validateValue(key, value);
  }

  const newPreferences = {
    ...(await readUserPreferences()),
    $schema: userPreferencesSchemaUrl,
  };
  if (deleteValue) {
    delete newPreferences[key];
  } else {
    newPreferences[key] = value;
  }

  return writeJson(getUserPreferencesPath(), newPreferences, { update: true, mode: 'permissive' });
}

function validateValue(key: keyof UserPreferences, value: string): string {
  switch (key) {
    case 'cachePath': {
      if (!value) {
        throw new Error('The cache folder cannot be empty.');
      }

      if (!path.isAbsolute(value)) {
        throw new Error('The cache folder must be an absolute path.');
      }

      const parentFolder = path.dirname(value);
      if (!isFolderSync(parentFolder)) {
        throw new Error(`The parent folder "${parentFolder}" of the cache folder does not exist.`);
      }

      // The folder is valid
      return value;
    }

    default: {
      // this gives a type error if a new preference is added
      const exhaustiveCheck: never = key;
      throw new Error(`Unhandled user preference: "${exhaustiveCheck}"`);
    }
  }
}
